// Data definitions for the Folk Objects homeowner survey

export const TOTAL_STEPS = 29;

export const STEP_LABELS = [
  '', 'Household basics', 'Household basics', 'Household basics', 'Household basics',
  'Household basics', 'Lifestyle & routines', 'Lifestyle & routines',
  'Entertaining', 'Entertaining', 'Work, rest & play', 'Work, rest & play',
  'Work, rest & play', 'Work, rest & play', 'Aesthetic & style', 'Color & texture',
  'Color & texture', 'Look & feel', 'Look & feel', 'Inspiration & values',
  'Inspiration & values', 'Inspiration & values', 'Inspiration & values',
  'Timeline', 'Working together', 'Working together', 'The feeling', 'Almost done', '',
];

// All questions are optional. Continue is always enabled.
export const REQUIRED_KEY_BY_STEP = {};

export const SPACE_OPTIONS = [
  'Kitchen', 'Living room', 'Primary bedroom', "Kids' rooms", 'Home office',
  'Outdoor & patio', 'Dining room', 'Bathrooms', 'Staff areas', 'Other',
];

export const CHRONOTYPE_OPTIONS = ['Early birds', 'Night owls', 'Somewhere in between'];

export const ENTERTAIN_OPTIONS = [
  'Casual family get-togethers',
  'Formal dinner parties',
  'Large parties & crowds',
  'We rarely host',
];

export const GUEST_GATHER_OPTIONS = [
  'Kitchen', 'Dining room', 'Living room', 'Balcony / outdoor', 'Somewhere else',
];

export const WORK_NEEDS_PEOPLE = [
  { key: 'workNeedsHusband', who: 'Husband' },
  { key: 'workNeedsWife', who: 'Wife' },
  { key: 'workNeedsKids', who: 'Kids' },
  { key: 'workNeedsOther', who: 'Other' },
];

export const WORK_NEEDS_OPTIONS = [
  'Needs a dedicated, closed-door office',
  'A quiet nook is enough',
  'Does not work from home',
];

export const UNWIND_OPTIONS = [
  'Listen to music', 'Dance', 'Paint', 'Just hang out', 'Watch TV',
  'Board games', 'Do fun chores together', 'Other',
];

export const PASSION_OPTIONS = [
  'No plastic / low-toxin materials',
  'Eco-first & sustainable',
  'Craft-first & handmade',
  'Supporting local makers',
  'Vintage & pre-loved over new',
  'Wellness & self-care spaces',
  'Smart home & tech-forward',
  'Art & collecting',
  'Multigenerational living',
  'Pet-friendly design',
  'None of these in particular',
];

export const EXPERIENCE_OPTIONS = [
  'Smooth & efficient. Just show me the results',
  'Collaborative. Involve me in every decision',
  'Fun. Take me shopping and exploring',
  'A mix of all of the above',
  'Something else',
];

export const STYLE_DEFS = [
  { value: 'modern', label: 'Modern / Minimalist', desc: 'Clean lines, sleek, clutter-free, functional.', slot: 'modern', hasRefs: true },
  { value: 'traditional', label: 'Traditional / Classic', desc: 'Timeless, symmetrical, elegant moldings, rich woods.', slot: 'traditional', hasRefs: true },
  { value: 'transitional', label: 'Transitional / Mid-century modern', desc: 'A blend of traditional comfort with clean, modern lines.', slot: 'transitional', hasRefs: true },
  { value: 'industrial', label: 'Industrial', desc: 'Raw materials, exposed brick, metal accents, urban vibe.', slot: 'industrial', hasRefs: true },
  { value: 'boho', label: 'Boho / Eclectic', desc: 'Layered textures, plants, global finds, relaxed and artistic.', slot: 'boho', hasRefs: true },
  { value: 'scandi', label: 'Scandinavian / Japandi', desc: 'Warm minimalism, natural woods, cozy textures (hygge).', slot: 'scandi', hasRefs: true },
  { value: 'coastal', label: 'Coastal / Organic Modern', desc: 'Bright, airy, natural fibers, earthy tones.', slot: 'coastal', hasRefs: true },
  { value: 'bali', label: 'Bali Tropical', desc: 'Bright, layered, with pops of accents among natural materials.', slot: 'bali', hasRefs: true },
  { value: 'tropicalmodern', label: 'Tropical Modern', desc: 'Sleek lines meet lush greenery: rattan, breezy resort-modern feel.', slot: 'tropicalmodern', hasRefs: true },
  { value: 'indoportuguese', label: 'Indo-Portuguese Resort', desc: 'Colonial-era architecture, dark wood, azulejo tiles, courtyards and verandas.', slot: 'indoportuguese', hasRefs: true },
  { value: 'indianeclectic', label: 'Indian Eclectic', desc: 'Vibrant textiles, brass accents, hand-block prints, layered heritage pieces.', slot: 'indianeclectic', hasRefs: true },
  { value: 'nychic', label: 'New York Chic', desc: 'Bold black-and-white stripes, graphic contrast, glamorous and confident.', slot: 'nychic', hasRefs: true },
];

export const COLOR_DEFS = [
  { value: 'moody', label: 'Moody & Dark', swatches: ['#23303B', '#2E2A26', '#3A2E4A'], imgs: ['/refs/color-moody-1.jpg', '/refs/color-moody-2.jpg'] },
  { value: 'neutral', label: 'Neutral & Earthy', swatches: ['#D9C9AE', '#BFA98A', '#8C7A5E'], imgs: ['/refs/color-neutral-1.jpg', '/refs/color-neutral-2.jpg'] },
  { value: 'bright', label: 'Bright & Airy', swatches: ['#FFFFFF', '#F2EFE9', '#DCE3E0'], imgs: ['/refs/color-bright-1.jpg', '/refs/color-bright-2.jpg'] },
  { value: 'bold', label: 'Bold & Vibrant', swatches: ['#C23B3B', '#2E7D6B', '#D69A2D'], imgs: ['/refs/color-bold-1.jpg', '/refs/color-bold-2.jpg'] },
  { value: 'pastel', label: 'Pastel & Soft', swatches: ['#F3D9DD', '#DCE8DA', '#DDE3EE'], imgs: ['/refs/color-pastel-1.jpg', '/refs/color-pastel-2.jpg'] },
  { value: 'mono', label: 'Monochrome & Tonal', swatches: ['#E7E3DC', '#B9B2A5', '#6E675C'], imgs: ['/refs/color-mono-1.jpg', '/refs/color-mono-2.jpg'] },
];

// Six either/or preference rows. Each of the 12 image slots below can be
// swapped by editing the aImg / bImg fields. Files live in
// /app/frontend/public/refs and are referenced with an absolute /refs/... path.
export const PREF_DEFS = [
  { key: 'furniture', question: 'Furniture feel', aLabel: 'Upholstered & comfy', bLabel: 'Sleek & minimal', aImg: '/refs/page12-1.png', bImg: '/refs/page12-2.png' },
  { key: 'pattern',   question: 'Solids or patterns', aLabel: 'Solids', bLabel: 'Patterns', aImg: '/refs/page12-4.png', bImg: '/refs/page12-3.png' },
  { key: 'motif',     question: 'Florals or geometrics', aLabel: 'Florals', bLabel: 'Geometrics', aImg: '/refs/page12-5.png', bImg: '/refs/page12-6.png' },
  { key: 'rug',       question: 'Rugs under furniture', aLabel: 'Anchored under furniture', bLabel: 'Furniture floats free', aImg: '/refs/eo-rug-a.jpg', bImg: '/refs/eo-rug-b.png' },
  { key: 'lighting',  question: 'Lighting mood', aLabel: 'Soft mood lighting', bLabel: 'Statement fixtures', aImg: '/refs/eo-lighting-a.jpg', bImg: '/refs/eo-lighting-b.jpg' },
  { key: 'walls',     question: 'Walls', aLabel: 'Wallpaper', bLabel: 'Paint', aImg: '/refs/eo-walls-a.jpg', bImg: '/refs/eo-walls-b.jpg' },
];

export const LABEL_MAP = {
  household: 'Who lives in the home',
  hasChildren: 'Has children',
  childCount: 'Number of children',
  childrenNotes: "Children's use of space",
  accessibility: 'Accessibility needs',
  spaces: 'Priority spaces',
  spacesOtherText: 'Other priority space',
  staffYesNo: 'Full-time staff',
  staffDetail: 'Staff detail',
  saturday: 'Saturday morning',
  weekday: 'Weekday morning',
  chronotype: 'Chronotype',
  lightNotes: 'Light notes',
  entertainStyle: 'Entertaining style',
  entertainFrequency: 'Entertaining frequency',
  cookPassions: 'Cooking/hosting passions',
  guestGather: 'Where guests gather',
  guestGatherOtherText: 'Other gather spot',
  workNeedsHusband: 'Husband, work from home',
  workNeedsWife: 'Wife, work from home',
  workNeedsKids: 'Kids, work from home',
  workNeedsOther: 'Other, work from home',
  unwind: 'How they unwind',
  unwindOtherText: 'Other unwind activity',
  workoutYesNo: 'Works out at home',
  workoutDetail: 'Workout detail',
  notWorking: "What isn't working",
  styles: 'Preferred styles',
  colorRelationship: 'Color relationship',
  colorsToAvoid: 'Colors to avoid',
  furniture: 'Furniture feel',
  pattern: 'Solids or patterns',
  motif: 'Florals or geometrics',
  rug: 'Rug preference',
  lighting: 'Lighting mood',
  walls: 'Walls',
  heirlooms: 'Heirlooms to include',
  travelInspo: 'Travel inspiration',
  artCraft: 'Art & craft styles',
  passions: 'Passions',
  oneWish: 'One thing to understand',
  deliveryDate: 'Flat delivery date',
  fixedInteriorsDate: 'Fixed interiors completion',
  moveInDate: 'Desired move-in date',
  experienceType: 'Desired experience',
  experienceOtherText: 'Experience detail',
  supportType: 'Type of support needed',
  fiveWords: 'Five words',
};
